/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: UserService.ts
 * Created by: chan.ying
 * Create Date: 2026-03-18 17:00
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import Result from "@/commons/Result";
import HttpRequestGet from "@/commons/https/HttpRequestGet";
import ApiUrlBuilder from "@/commons/https/ApiUrlBuilder";
import AppConfig from "@/commons/AppConfig";
import HttpExecutor from "@/commons/https/HttpExecutor";
import HttpTokenProvider from "@/commons/https/HttpTokenProvider";
import Log from "@/commons/logs/Log";
import {buildPageParams, PaginationResponse} from "@/commons/utils/PageUtil";
import UserInfoVO from "@/mvc/models/team/UserInfoVO";
import HttpRequestPostData from "@/commons/https/HttpRequestPostData";
import HttpRequestPost from "@/commons/https/HttpRequestPost";
import HttpRequestPut from "@/commons/https/HttpRequestPut";
import {UserProfileInfo} from "@/mvc/models/team/UserInfoVO";
import {GroupInfoVO} from "@/mvc/models/team/GroupInfoVO";
import {HttpStatus} from "@/commons/https/HttpStatus";
import DeviceInfoVO from "@/mvc/models/team/DeviceInfoVO";

export default class UserService {

    // ------------------------------------------------------------------------------------
    // singleton
    // ------------------------------------------------------------------------------------
    private static _instance: UserService;

    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    // ------------------------------------------------------------------------------------
    private constructor() {
    }

    /**
     * Retrieves a list of items based on the provided filters and pagination parameters.
     *
     * @param {string} [search] - Optional search query to filter results.
     * @param {string[]} [state] - Optional array of states to filter results.
     * @param {string} [after] - Optional parameter for pagination to fetch results after a specified cursor.
     * @param {string} [before] - Optional parameter for pagination to fetch results before a specified cursor.
     * @return {Promise<Result<PaginationResponse<UserInfoVO>>>} A promise that resolves to a Result object containing the paginated response data.
     */
    public list(search?: string, state?: string[], after?: string, before?: string): Promise<Result<PaginationResponse<UserInfoVO>>> {
        const params = buildPageParams({
            search: search,
            after: after,
            before: before,
            state: state,
        })

        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, params);
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<PaginationResponse<UserInfoVO>>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on list user, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<PaginationResponse<UserInfoVO>>(status));
            });
        })
    }

    /**
     * Retrieves user information for the specified user ID.
     *
     * @param {string} userId - The unique identifier of the user whose information is to be retrieved.
     * @return {Promise<Result<UserInfoVO>>} A promise containing a Result object that either holds the retrieved user information (UserInfoVO) on success or indicates an error status on failure.
     */
    public retrieve(userId: string): Promise<Result<UserInfoVO>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<UserInfoVO>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on retrieve user, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<UserInfoVO>(status));
            });
        })
    }

    /**
     * Creates a new user by sending a POST request with the provided user data.
     *
     * @param {UserInfoVO} data The user creation request information containing required details for the new user.
     * @return {Promise<Result<UserInfoVO>>} A promise that resolves to a Result object containing the status and response data
     * or an error status if the request fails.
     */
    public create(data: UserInfoVO): Promise<Result<UserInfoVO>> {
        let req = new HttpRequestPostData(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, data);
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<UserInfoVO>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on create user, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<UserInfoVO>(status));
            });
        })
    }

    /**
     * Changes the state of a user based on the provided userId and state value.
     *
     * @param {string} userId - The unique identifier of the user whose state is to be changed.
     * @param {number} state - The new state value to be assigned to the user.
     * @return {Promise<Result<void>>} A promise that resolves to a Result object containing the status of the operation.
     */
    public changeState(userId: string, state: number): Promise<Result<void>> {
        const params = new URLSearchParams()
        params.append("userState", state.toString())
        let req = new HttpRequestPut(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}/state`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, null, params);
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<void>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on change user state, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<void>(status));
            });
        })
    }

    /**
     * Changes the username of the specified user.
     *
     * @param {string} userId - The unique identifier of the user whose name is to be changed.
     * @param {string} name - The new name to set for the user.
     * @return {Promise<Result<void>>} A promise that resolves to a Result indicating the success or failure of the operation.
     */
    public changeName(userId: string, name: string): Promise<Result<void>> {
        const params = new URLSearchParams()
        params.append("username", name)
        let req = new HttpRequestPut(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}/username`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, null, params);
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<void>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on change user name, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<void>(status));
            });
        })
    }

    /**
     * Fetches a list of user briefs containing userId and userEmail.
     *
     * @return {Promise<Result<{ userId: string, userEmail: string }[]>>} A promise resolving to a result containing an array of objects with userId and userEmail, or an error status if the operation fails.
     */
    public listBrief(): Promise<Result<{ userId: string, userEmail: string }[]>> {
        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/brief`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            });
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<{ userId: string, userEmail: string }[]>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on list user brief, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<{ userId: string, userEmail: string }[]>(status));
            });
        })
    }

    /**
     * Retrieves a paginated list of user groups for a specified user.
     *
     * @param {string} userId The unique identifier of the user whose groups need to be listed.
     * @param {string} [after] The cursor for fetching records after a specific point. Optional.
     * @param {string} [before] The cursor for fetching records before a specific point. Optional.
     * @return {Promise<Result<PaginationResponse<GroupInfoVO>>>} A promise that resolves to a result containing a pagination response with the list of user groups.
     */
    public listUserGroup(userId: string, after?: string, before?: string): Promise<Result<PaginationResponse<GroupInfoVO>>> {
        const params = buildPageParams({
            after: after,
            before: before,
        })

        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}/groups`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, params);
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<PaginationResponse<GroupInfoVO>>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on list user group, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<PaginationResponse<GroupInfoVO>>(status));
            });
        })
    }

    /**
     * Lists devices associated with a specific user.
     *
     * @param {string} userId - The unique identifier of the user
     * @param {string} [after] - Optional cursor for pagination (after position)
     * @param {string} [before] - Optional cursor for pagination (before position)
     * @returns {Promise<Result<PaginationResponse<DeviceInfoVO>>>} Promise resolving to paginated user devices result
     */
    public listUserDevice(userId: string, after?: string, before?: string): Promise<Result<PaginationResponse<DeviceInfoVO>>> {
        const params = buildPageParams({
            after: after,
            before: before,
        })

        let req = new HttpRequestGet(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}/devices`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, params);
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<PaginationResponse<DeviceInfoVO>>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on list user device, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<PaginationResponse<DeviceInfoVO>>(status));
            });
        })
    }


    /**
     * Updates the user profile information.
     *
     * @param {string} userId - The unique identifier of the user
     * @param {UserProfileInfo} userProfileInfo - The updated user profile information
     * @return {Promise<Result<void>>} A promise that resolves to a Result object containing the update result.
     */
    public updateUserProfile(userId: string, userProfileInfo: UserProfileInfo): Promise<Result<void>> {
        let req = new HttpRequestPut(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}/profile-setting`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, userProfileInfo.toJSON());
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<void>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on update user profile setting, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<void>(status));
            });
        })
    }

    /**
     * Updates the user avatar by uploading an image file.
     *
     * @param {string} userId - The unique identifier of the user.
     * @param {File} file - The avatar image file to upload.
     * @return {Promise<Result<string>>} A promise that resolves to a Result containing the avatar URL on success.
     */
    public updateAvatar(userId: string, file: File): Promise<Result<string>> {
        const formData = new FormData();
        formData.append('avatar', file);

        let req = new HttpRequestPost(ApiUrlBuilder.InstanceTeam,
            `/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}/avatar`,
            {
                timeout: AppConfig.Instance.apiDefaultTimeout,
            }, formData);
        return new Promise(resolve => {
            HttpExecutor.Instance.execute(req, HttpTokenProvider.TeamService).then((res) => {
                resolve(Result.of<string>(res.status, res.data));
                return;
            }).catch((err) => {
                Log.error("error on update avatar, cause: " + err);
                const status = err?.response?.status ?? HttpStatus.StatusBadGateway;
                resolve(Result.of<string>(status));
            });
        })
    }

    /**
     * Retrieves the avatar URL for a specific user.
     *
     * @param {string} userId - The unique identifier of the user
     * @returns {string} The avatar URL for the user
     */
    public getAvatar(userId: string): string {
        return ApiUrlBuilder.InstanceTeam.resolve(`/cld-service-teamsite/api/v1/users/${ApiUrlBuilder.seg(userId)}/avatar`);
    }
}
